import React from 'react';
import { useEffect, useRef } from "react";
import * as d3 from "d3";
import './RadarChart.scss';

export default function RadarChart({ data = [], width = 350, height = 350, levels = 5 }) {
    const svgRef = useRef(null);

    useEffect(() => {
        if (!svgRef.current) return;

        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const defs = svg.append("defs");

        const radius = Math.min(width, height) / 2 - 90;
        const centerX = width / 2;
        const centerY = height / 2;
        const angleSlice = (Math.PI * 2) / data.length;

        // Gradient for data pointer
        const gradient = defs
            .append("radialGradient")
            .attr("id", "radarGradient")
            .attr("gradientUnits", "objectBoundingBox")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "100%")
            .attr("y2", "0%"); // 90deg

        gradient.append("stop")
            .attr("offset", "0%")
            .attr("stop-color", "#B3CAE2");

        gradient.append("stop")
            .attr("offset", "50%")
            .attr("stop-color", "#FFC7D2");

        gradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "#FFF0C0");

        // Gradient for outer dots
        const maxDotGradient = defs
            .append("linearGradient")
            .attr("id", "maxDotGradient")
            .attr("gradientUnits", "objectBoundingBox")
            .attr("x1", "0%")
            .attr("y1", "0%")
            .attr("x2", "0%")
            .attr("y2", "100%"); // vertical (≈ 180deg)

        maxDotGradient.append("stop")
            .attr("offset", "3.2%")
            .attr("stop-color", "#EED4A1");

        maxDotGradient.append("stop")
            .attr("offset", "70.12%")
            .attr("stop-color", "#CFB58D");

        maxDotGradient.append("stop")
            .attr("offset", "100%")
            .attr("stop-color", "#FFF5E1");

        const g = svg
            .append("g")
            .attr("transform", `translate(${centerX}, ${centerY})`);

        // Per-axis scales
        const scales = data.map(d =>
            d3.scaleLinear().domain([d.minScore, d.maxScore]).range([0, radius])
        );

        // Grid Polygons
        for (let level = 1; level <= levels; level++) {
            const levelRatio = level / levels;
            const curvePoints = [];

            data.forEach((axis, i) => {
                const angle = i * angleSlice - Math.PI / 2;
                const midAngle = angle + angleSlice / 2;

                // Current Axis Point
                const r = scales[i](axis.minScore + (axis.maxScore - axis.minScore) * levelRatio);
                curvePoints.push([r * Math.cos(angle), r * Math.sin(angle)]);

                // INVERSION POINT: Calculate a point in the middle of two axes
                // We multiply 'r' by a factor (e.g., 0.85) to create the "dip"
                const dipFactor = 0.75;
                const dipR = r * dipFactor;
                curvePoints.push([dipR * Math.cos(midAngle), dipR * Math.sin(midAngle)]);
            });

            // Use d3.lineRadial() with a curve to make it smooth
            const radarLine = d3.line()
                .x(d => d[0])
                .y(d => d[1])
                .curve(d3.curveCardinalClosed.tension(0.3)); // Tension adjusts how "sharp" the points are

            g.append("path")
                .attr("d", radarLine(curvePoints))
                .attr("class", "radar-grid")
        }

        const polygonPoints = [];

        // Axis lines + labels
        data.forEach((axis, i) => {
            const angle = i * angleSlice - Math.PI / 2;

            // Calculate data point position
            const r = scales[i](axis.value);
            const pointX = r * Math.cos(angle);
            const pointY = r * Math.sin(angle);
            const maxR = scales[i](axis.maxScore);
            const maxX = maxR * Math.cos(angle);
            const maxY = maxR * Math.sin(angle);
            polygonPoints.push([pointX, pointY]);

            // Draw axis line
            g.append("line")
                .attr("class", "radar-axis")
                .attr("x1", 0)
                .attr("y1", 0)
                .attr("x2", radius * Math.cos(angle))
                .attr("y2", radius * Math.sin(angle));

            // Draw radar polygon points (optional polygon will be drawn separately)
            g.append("circle")
                .attr("class", "radar-dot")
                .attr("cx", pointX)
                .attr("cy", pointY)
                .attr("r", 6);

            g.append("circle")
                .attr("class", "radar-max-dot")
                .attr("cx", maxX)
                .attr("cy", maxY)
                .attr("r", 7)

            // Label base position
            let labelRadius = radius + 28;
            let labelX = labelRadius * Math.cos(angle);
            let labelY = labelRadius * Math.sin(angle);

            // Fine-tune labels per quadrant (matches image layout)
            if (angle < -Math.PI / 2 || angle > Math.PI / 2) {
                labelX -= 10; // left side
            } else {
                labelX += 10; // right side
            }

            if (angle < 0 && angle > -Math.PI) {
                labelY += 6; // top
            } else {
                labelY += 6; // bottom
            }

            // Draw label
            g.append("text")
                .attr("class", "radar-label")
                .attr("x", labelX)
                .attr("y", labelY)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
                .text(axis.label);

        });

        // Radar points
        const points = data.map((d, i) => {
            const angle = i * angleSlice - Math.PI / 2;
            const r = scales[i](d.value);
            return [r * Math.cos(angle), r * Math.sin(angle)];
        });

        // Polygon
        g.append("polygon")
            .attr("points", polygonPoints.map(p => p.join(",")).join(" "))
            .attr("class", "radar-area");

        // Data points
        g.selectAll(".radar-dot")
            .data(points)
            .enter()
            .append("circle")
            .attr("class", "radar-dot")
            .attr("cx", d => d[0])
            .attr("cy", d => d[1])
            .attr("r", 6);
    }, [data, width, height]);

    return (
        <div className="radar-container" style={{ width: width }}>
            <svg
                ref={svgRef}
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
            />
        </div>
    );
}
